/**
 * 광고 효율 진단 페이지 (MMM-lite)
 *
 * 광고비(몰별 설정 채널, 기본 Meta) × 자사몰 매출로 베이스라인·한계 ROAS·포화·이월·요일 효과를 보여 준다.
 * 계산과 판정 게이트는 백엔드(`v2/media_mix`)가 하고, 이 페이지는 그리기만 한다.
 * 판정 정본: `agent_docs/mmm_readiness.md`.
 */
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, Loader2 } from 'lucide-react'

import { useGetMediaMixDiagnosis } from '@/api/generated/v2-media-mix/v2-media-mix'
import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Card, CardContent } from '@/components/ui/Card'
import { useShop } from '@/lib/shopContext'
import { MediaMixReport } from '@/features/media-mix/MediaMixReport'

export const Route = createFileRoute('/_dashboard/media-mix')({
  component: MediaMixPage,
})

function MediaMixPage() {
  // ⭐ 허용 목록 밖이면 **제목도 띄우지 않고** 잘라낸다. 제목과 설명을 렌더한 뒤 404 문구를 붙이면
  //    403 을 피한 이유(기능의 존재를 알리지 않는다)가 화면에서 무너진다(리뷰 지적). 쿼리도 안 나간다.
  //    ⚠️ 이 플래그는 노출용이다 — 실제 차단은 계속 API 404 가 진다.
  const { shop } = useShop()
  const enabled = !!shop?.media_mix_enabled

  // 요청마다 계산하지만 값은 하루 한 번(일배치 이후)만 바뀐다 — 창 전환마다 다시 부르지 않는다.
  const { data, isLoading, isError, error } = useGetMediaMixDiagnosis({
    query: {
      enabled,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      // 404(허용 목록 밖)만 재시도를 끊는다 — 일시적 5xx·네트워크 오류는 그대로 재시도한다(리뷰 지적).
      retry: (count, err) => (err as { status?: number } | null)?.status !== 404 && count < 3,
    },
  })
  // 플래그가 늦게 반영되는 경우(샵 응답 캐시 등)에도 404 면 같은 화면으로 떨어뜨린다.
  const notFound =
    (error as { status?: number; response?: { status?: number } } | null)?.status === 404 ||
    (error as { response?: { status?: number } } | null)?.response?.status === 404

  if (!enabled || notFound) return <HiddenFeatureNotFound />

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-on-surface">광고 효율 진단</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          {/* 채널 이름을 박지 않는다 — 몰마다 합산 채널이 다르다(치순당 = Meta·구글). 채널은 바로 아래 기간 줄이 말한다. */}
          광고비와 자사몰 매출로, 광고가 매출을 얼마나 움직였는지 추정해요.
        </p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16" aria-live="polite">
          <Loader2 className="h-6 w-6 animate-spin text-on-surface-variant" aria-label="불러오는 중" />
        </div>
      )}
      {isError && (
        <Card>
          <CardContent className="flex gap-3 py-5">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-error" aria-hidden />
            <p className="text-sm text-on-surface">진단을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</p>
          </CardContent>
        </Card>
      )}
      {data && <MediaMixReport data={data} />}
    </div>
  )
}
